import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  IconButton, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  TextField, 
  Select, 
  MenuItem,
  useTheme,
  Grid,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TableContainer
} from '@mui/material';

import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { 
  Add as AddIcon, 
  Check as CheckIcon, 
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Block as BlockIcon
} from '@mui/icons-material';

const AgendaDiaria = () => {
  const theme = useTheme();
  const [agenda, setAgenda] = useState([]);
  const [nombrePaciente, setNombrePaciente] = useState('');
  const [medico, setMedico] = useState('');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [editMedico, setEditMedico] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  

  const API_URL = process.env.REACT_APP_API_URL;
  // Handler para marcar como "Abandonó la espera"
  const handleAbandonoEspera = async (id) => {
    try {
      await axios.put(`${API_URL}/api/agenda/${id}/abandono-espera`);
      await fetchAgenda();
    } catch (err) {
      console.error('Error marcando abandono de espera:', err);
      setError('Error al marcar abandono de la espera. Por favor, intente nuevamente.');
    }
  };

  const fetchAgenda = useCallback(async () => {
  try {
    setLoading(true);
    const fecha = selectedDate.format('YYYY-MM-DD');
    const response = await axios.get(`${API_URL}/api/agenda`, {
      params: { fecha }
    });
    
    // Asegurarse de que el estado recibido sea válido, incluyendo 'Abandonó la espera'
    const validStates = ['En espera', 'En consulta', 'Finalizado', 'Abandonó la espera'];
    const sortedAgenda = response.data.map(item => ({
      ...item,
      estado: validStates.includes(item.estado) ? item.estado : 'En espera'
    })).sort((a, b) => new Date(b.hora_ingreso) - new Date(a.hora_ingreso));

    console.log('Datos recibidos de la API:', sortedAgenda);
    setAgenda(sortedAgenda);
    setError('');
  } catch (err) {
    console.error('Error fetching agenda:', err);
    setError('Error al cargar la agenda. Por favor, intente nuevamente.');
  } finally {
    setLoading(false);
  }
}, [selectedDate, API_URL]);

  useEffect(() => {
    fetchAgenda();
  }, [fetchAgenda]);

  const handleAddPaciente = async () => {
    if (!medico) {
      setError('Médico es requerido');
      return;
    }

    if (!nombrePaciente) {
      setError('Nombre del paciente es requerido');
      return;
    }

    try {
      const fecha = selectedDate.format('YYYY-MM-DD');
      const response = await axios.post(`${API_URL}/api/agenda`, {
        nombre_paciente: nombrePaciente,
        medico,
        fecha,
        estado: 'En espera'
      });
      
      setAgenda(prevAgenda => [response.data, ...prevAgenda]);
      setNombrePaciente('');
      setMedico('');
    } catch (err) {
      console.error('Error adding patient:', err);
      setError('Error al agregar el paciente. Por favor, intente nuevamente.');
    }
  };

  const handleIniciarConsulta = async (id) => {
    try {
      // First send the request to the server
      await axios.put(`${API_URL}/api/agenda/${id}/iniciar-consulta`);
      
      // Then fetch the updated data from the server
      await fetchAgenda();
    } catch (err) {
      console.error('Error iniciando consulta:', err);
      setError('Error al iniciar la consulta. Por favor, intente nuevamente.');
    }
  };

  const handleEgreso = async (id) => {
    try {
      // First send the request to the server
      await axios.put(`${API_URL}/api/agenda/${id}/egreso`);
      
      // Then fetch the updated data from the server
      await fetchAgenda();
    } catch (err) {
      console.error('Error actualizando egreso:', err);
      setError('Error al registrar el egreso. Por favor, intente nuevamente.');
    }
  };

  const handleEditSave = async (id) => {
    try {
      // Actualización optimista durante la edición
      setAgenda(prevAgenda => 
        prevAgenda.map(item => 
          item.id === id ? { 
            ...item, 
            nombre_paciente: editNombre,
            medico: editMedico
          } : item
        )
      );
      
      // Enviar cambios al servidor
      await axios.put(`${API_URL}/api/agenda/${id}`, {
        nombre_paciente: editNombre,
        medico: editMedico
      });
      
      setEditingId(null);
    } catch (err) {
      // Revertir cambios si hay error
      fetchAgenda();
      console.error('Error updating patient:', err);
      setError('Error al actualizar el paciente. Por favor, intente nuevamente.');
    }
  };

  const handleDelete = async (id) => {
    try {
      // Eliminación optimista
      setAgenda(prevAgenda => prevAgenda.filter(item => item.id !== id));
      
      await axios.delete(`${API_URL}/api/agenda/${id}`);
      setDeleteDialogOpen(false);
    } catch (err) {
      // Revertir eliminación si hay error
      fetchAgenda();
      console.error('Error deleting patient:', err);
      setError('Error al eliminar el paciente. Por favor, intente nuevamente.');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calcular los elementos para la página actual
  const currentItems = agenda.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


// Función para obtener el color del estado
const getEstadoColor = (estado) => {
  switch(estado) {
    case 'En espera':
      return theme.palette.warning.main;
    case 'En consulta':
      return theme.palette.primary.main;
    case 'Finalizado':
      return theme.palette.success.main;
    case 'Abandonó la espera':
      return '#8e24aa'; // Morado para abandono
    default:
      return 'inherit';
  }
};

  // Reemplazar la función handleMedicoChange actual por esta versión simple
  const handleMedicoChange = (e) => {
    setMedico(e.target.value);
  };

  return (
    <Box sx={{ 
        display: 'flex',
        padding: '64px 16px 16px 16px',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        minHeight: 'calc(100vh - 64px)',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        position: 'relative',
        boxSizing: 'border-box',
        margin: 0
    }}>
      <Paper
        elevation={3}
        sx={{
            padding: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            borderRadius: 2,
            margin: '0',
            boxSizing: 'border-box',
            background: `linear-gradient(135deg, ${theme.palette.background.default} 0%,rgb(238, 245, 178) 100%)`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            height: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            marginTop: '450px',
            overflow: 'visible'
        }}
      >
        <Box sx={{ 
            display: 'flex',
            padding: '16px',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            minHeight: 'auto',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            position: 'relative',
            boxSizing: 'border-box',
            margin: 0
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                color: theme.palette.primary.main,
                mb: { xs: 2, sm: 0 }
              }}>
                Agenda Diaria
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Seleccionar Fecha"
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    format="DD/MM/YYYY"
                    sx={{ flex: 1 }}
                  />
                </LocalizationProvider>
                <Button 
                  variant="contained" 
                  startIcon={<RefreshIcon />} 
                  onClick={fetchAgenda}
                  sx={{ minWidth: 120, height: '56px', ml: 1 }}
                >
                  Actualizar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Formulario para agregar paciente */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                label="Nombre del Paciente"
                fullWidth
                value={nombrePaciente}
                onChange={(e) => setNombrePaciente(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Select
                label="Médico"
                fullWidth
                value={medico}
                onChange={handleMedicoChange}
                displayEmpty
              >
                <MenuItem value="" disabled>Seleccione médico</MenuItem>
                <MenuItem value="Dr. Carlos Espinoza">Dr. Carlos Espinoza</MenuItem>
                <MenuItem value="Dra. Maria Martha Espinoza">Dra. Maria Martha Espinoza</MenuItem>
                <MenuItem value="Dra. Samanta Espinoza">Dra. Samantha Espinoza</MenuItem>
                <MenuItem value="Visitador Médico">Visitador Médico</MenuItem>
                <MenuItem value="Pendiente">Pendiente</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleAddPaciente}
                fullWidth
                sx={{ height: '56px' }}
              >
                Agregar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Nombre del Paciente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Hora Ingreso</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Médico</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Hora Egreso</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : currentItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    No hay pacientes registrados para esta fecha.
                  </TableCell>
                </TableRow>
              ) : (
                currentItems.map((row) => (
                  <TableRow key={row.id}>
                    {editingId === row.id ? (
                      // Modo edición
                      <>
                        <TableCell>
                          <TextField
                            value={editNombre}
                            onChange={(e) => setEditNombre(e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {dayjs(row.hora_ingreso).format('HH:mm')}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={editMedico}
                            onChange={(e) => setEditMedico(e.target.value)}
                            fullWidth
                            size="small"
                          >
                            <MenuItem value="Dr. Carlos Espinoza">Dr. Carlos Espinoza</MenuItem>
                            <MenuItem value="Dra. Maria Martha Espinoza">Dra. Maria Martha Espinoza</MenuItem>
                            <MenuItem value="Dra. Samanta Espinoza">Dra. Samantha Espinoza</MenuItem>
                            <MenuItem value="Visitador Médico">Visitador Médico</MenuItem>
                            <MenuItem value="Pendiente">Pendiente</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: getEstadoColor(row.estado),
                              fontWeight: 'bold'
                            }}
                          >
                            {row.estado}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.estado === 'Finalizado' && row.hora_egreso 
                            ? dayjs(row.hora_egreso).format('HH:mm') 
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="contained" 
                            color="primary" 
                            size="small"
                            onClick={() => handleEditSave(row.id)}
                            sx={{ mr: 1 }}
                          >
                            Guardar
                          </Button>
                          <Button 
                            variant="outlined" 
                            color="secondary" 
                            size="small"
                            onClick={() => setEditingId(null)}
                          >
                            Cancelar
                          </Button>
                        </TableCell>
                      </>
                    ) : (
                      // Modo visualización
                      <>
                        <TableCell>{row.nombre_paciente}</TableCell>
                        <TableCell>
                          {dayjs(row.hora_ingreso).format('HH:mm')}
                        </TableCell>
                        <TableCell>{row.medico}</TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: getEstadoColor(row.estado),
                              fontWeight: 'bold'
                            }}
                          >
                            {row.estado}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.estado === 'Finalizado' && row.hora_egreso 
                            ? dayjs(row.hora_egreso).format('HH:mm') 
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {row.estado === 'En espera' && (
                            <>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleIniciarConsulta(row.id)}
                                title="Iniciar consulta"
                              >
                                <PlayArrowIcon />
                              </IconButton>
                              <IconButton
                                color="secondary"
                                onClick={() => handleAbandonoEspera(row.id)}
                                title="Abandonó la espera"
                                sx={{ color: '#8e24aa' }} // Color morado para el botón
                              >
                                <BlockIcon />
                              </IconButton>
                            </>
                          )}
                          {row.estado === 'En consulta' && (
                            <IconButton 
                              color="success" 
                              onClick={() => handleEgreso(row.id)}
                              title="Finalizar consulta"
                            >
                              <CheckIcon />
                            </IconButton>
                          )}
                          <IconButton 
                            color="primary" 
                            onClick={() => {
                              setEditingId(row.id);
                              setEditNombre(row.nombre_paciente);
                              setEditMedico(row.medico);
                            }}
                            title="Editar paciente"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => {
                              setDeleteId(row.id);
                              setDeleteDialogOpen(true);
                            }}
                            title="Eliminar paciente"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={agenda.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TableContainer>
      </Paper>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar este paciente de la agenda?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancelar
          </Button>
          <Button 
            onClick={() => handleDelete(deleteId)} 
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgendaDiaria;